import api from './client';
import type {
  BillingAccessResponse,
  BillingCheckoutResponse,
  BillingContactResponse,
  BillingInvoice,
  BillingInvoiceListParams,
  BillingInvoicePageResponse,
  BillingInvoiceResponse,
  CompanyBillingOverviewResponse,
  UpdateBillingContactPayload,
} from '@gokrew/models/billing';
import type { DownloadedFile } from '@/types/performance';

/* ---------------------------------- */
/* 🔧 Endpoints */
/* ---------------------------------- */

const base = (companyId: string) => `/companies/${companyId}/billing`;

const endpoints = {
  access: (companyId: string) => `${base(companyId)}/access`,
  overview: (companyId: string) => base(companyId),
  contact: (companyId: string) => `${base(companyId)}/contact`,
  checkout: (companyId: string) => `${base(companyId)}/payment-method/checkout`,
  invoices: (companyId: string) => `${base(companyId)}/invoices`,
  invoicePdf: (companyId: string, invoiceId: string) =>
    `${base(companyId)}/invoices/${invoiceId}/pdf`,
  sendInvoice: (companyId: string, invoiceId: string) =>
    `${base(companyId)}/invoices/${invoiceId}/send`,
};

/* ---------------------------------- */
/* 💳 Plan and payment method */
/* ---------------------------------- */

/** Whether billing applies to the company (any company admin may ask). */
export const getBillingAccess = async (companyId: string) => {
  const { data } = await api.get<BillingAccessResponse>(
    endpoints.access(companyId)
  );
  return data;
};

/** Plan, payment method and billing contact (`billing.read`). */
export const getBillingOverview = async (companyId: string) => {
  const { data } = await api.get<CompanyBillingOverviewResponse>(
    endpoints.overview(companyId)
  );
  return data;
};

export const updateBillingContact = async (
  companyId: string,
  payload: UpdateBillingContactPayload
) => {
  const { data } = await api.patch<BillingContactResponse>(
    endpoints.contact(companyId),
    payload
  );
  return data;
};

/** Stripe Checkout (setup mode); the caller sends the browser to `url`. */
export const startPaymentMethodCheckout = async (companyId: string) => {
  const { data } = await api.post<BillingCheckoutResponse>(
    endpoints.checkout(companyId)
  );
  return data;
};

/* ---------------------------------- */
/* 🧾 Invoices */
/* ---------------------------------- */

export const getBillingInvoices = async (
  companyId: string,
  params: BillingInvoiceListParams
) => {
  const { data } = await api.get<BillingInvoicePageResponse>(
    endpoints.invoices(companyId),
    { params }
  );
  return data;
};

/** The invoice PDF, named as the server names it (`Invoice-KHR-000001.pdf`). */
export const downloadBillingInvoicePdf = async (
  companyId: string,
  invoice: Pick<BillingInvoice, 'id' | 'number'>
): Promise<DownloadedFile> => {
  const response = await api.get<Blob>(
    endpoints.invoicePdf(companyId, invoice.id),
    { responseType: 'blob' }
  );
  const disposition = response.headers['content-disposition'] as
    string | undefined;
  const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1];
  return { blob: response.data, filename: filename ?? `${invoice.number}.pdf` };
};

/** Emails the invoice PDF to the billing contact's accounting email. */
export const sendBillingInvoice = async (
  companyId: string,
  invoiceId: string
) => {
  const { data } = await api.post<BillingInvoiceResponse>(
    endpoints.sendInvoice(companyId, invoiceId)
  );
  return data;
};
